import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, BookOpen, Loader2, Plus, Undo2, X } from 'lucide-react';
import { pentestsAPI } from '../../services/api';

/**
 * What the operator already knows, handed to the agents.
 *
 * An agent can only act on what the ledger shows it, and recon only finds what
 * a wordlist guesses. Everything else the operator knows used to have nowhere
 * to go: the same fact about this target's API prefix was written into the
 * rules of engagement twice and the Lead acted on neither, because prose in a
 * notes field is not something a plan can cite or a probe can fire.
 *
 * A fact is STATED, never measured. It changes what an agent is able to test
 * and never what counts as proof.
 */

/**
 * Worked examples behind a (?), for the same reason the identity vault has
 * them. "Statement" and "values" are honest field names and neither tells an
 * operator what is actually useful to paste.
 */
const EXAMPLES = [
  {
    label: 'verified API routes',
    statement:
      'These paths exist. An existing route answers 403 with a short JSON body; '
      + 'an absent one returns the 4877-byte SPA shell.',
    values: ['v1/users', 'v1/account-attributes', 'v1/sub-accounts'],
    why: 'The strongest kind. An agent fires the whole list as one probe population without retyping any of it.',
  },
  {
    label: 'API prefix',
    statement:
      'The API is served at /v1 and /v2 on the same host, with no /api prefix. '
      + 'Anything under /api/ hits the SPA catch-all.',
    values: [],
    why: 'No values, and still the most useful thing anyone can say about a target. It stops six engagements testing a catch-all.',
  },
  {
    label: 'tenant id parameter',
    statement: 'Every /v1 endpoint accepts an accountId query parameter that is not in the UI.',
    values: ['accountId', 'subAccountId'],
    why: 'Parameter names are a population too. This is how an IDOR angle gets a payload set.',
  },
];

function ExamplesHelp() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="What counts as a fact?"
        className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px] font-bold text-slate-500 hover:border-wasabi-400 hover:text-wasabi-700"
      >
        ?
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-xs">
          <p className="mb-2 leading-snug text-slate-600">
            Anything true about the target that a scanner would not find on its own:
            an API surface, a parameter nobody documents, a routing quirk. The
            statement is what the agents read. The values are the machine-usable
            half, and an agent can fire a whole list as one probe population
            without retyping it.
          </p>
          <ul className="space-y-2">
            {EXAMPLES.map((e) => (
              <li key={e.label} className="border-t border-slate-100 pt-2">
                <div className="font-semibold text-slate-700">{e.label}</div>
                <div className="text-slate-500">{e.statement}</div>
                {e.values.length > 0 && (
                  <div className="mt-0.5">
                    <span className="text-slate-400">values </span>
                    <code className="rounded bg-slate-100 px-1 text-[11px]">
                      {e.values.join(', ')}
                    </code>
                  </div>
                )}
                <div className="mt-0.5 italic text-slate-400">{e.why}</div>
              </li>
            ))}
          </ul>
          <p className="mt-2 border-t border-slate-100 pt-2 leading-snug text-slate-500">
            A fact is not evidence. It tells an agent where to look and never
            settles anything: a claim still has to be proved with a probe, and
            the report says plainly which findings rest on something you stated.
          </p>
        </div>
      )}
    </>
  );
}

const EMPTY = { label: '', statement: '', values: '' };

function AddFactForm({ onSubmit, onCancel, busy }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // One per line, because that is the shape of everything an operator pastes:
  // a grep result, a column from a spreadsheet, a list out of a browser
  // console. Splitting on commas would break the first value containing one.
  const parsed = form.values.split('\n').map((v) => v.trim()).filter(Boolean);
  const valid = form.label.trim() && form.statement.trim();

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="grid gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Label</label>
          <input
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="verified API routes"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">
            Statement
            <span className="ml-1 font-normal text-slate-400">
              what you are vouching for, in your own words
            </span>
          </label>
          <textarea
            value={form.statement}
            onChange={(e) => set('statement', e.target.value)}
            rows={3}
            placeholder="These paths exist on the API. An existing route answers 403 with a short JSON body; an absent one returns the SPA shell."
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">
            Values
            <span className="ml-1 font-normal text-slate-400">
              optional, one per line. An agent can fire these as a probe population.
            </span>
          </label>
          <textarea
            value={form.values}
            onChange={(e) => set('values', e.target.value)}
            rows={5}
            placeholder={'v1/users\nv1/account-attributes\nv1/sub-accounts'}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-xs focus:border-wasabi-500 focus:outline-none focus:ring-1 focus:ring-wasabi-500"
          />
          {parsed.length > 0 && (
            <p className="mt-1 text-[11px] text-slate-400">
              {parsed.length} value(s), sent exactly as written
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => onSubmit({
            label: form.label.trim(),
            statement: form.statement.trim(),
            values: parsed,
          })}
          className="inline-flex items-center gap-1.5 rounded-md bg-wasabi-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-wasabi-600 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Save fact
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-800">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function KnownFacts({ pentestId, isPentester, onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Withdrawn rows are hidden by default, matching what the agents see, and
  // revealable because "I withdrew that one" is worth being able to check
  // against a report that still quotes it.
  const [showRetracted, setShowRetracted] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const next = await pentestsAPI.facts.list(pentestId, { include_retracted: true });
      setRows(next);
      onCountChange?.(next.filter((r) => !r.retracted_at).length);
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? 'Known facts are visible to Red Teamers only.'
          : (e?.response?.data?.detail || 'Could not load known facts.'),
      );
    } finally {
      setLoading(false);
    }
  }, [pentestId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      await pentestsAPI.facts.create(pentestId, payload);
      setAdding(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save the fact.');
    } finally {
      setBusy(false);
    }
  };

  const retract = async (row) => {
    setBusy(true);
    setError(null);
    try {
      await pentestsAPI.facts.retract(pentestId, row.id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not withdraw the fact.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading known facts...
      </div>
    );
  }

  const live = rows.filter((r) => !r.retracted_at);
  const withdrawn = rows.filter((r) => r.retracted_at);
  const shown = showRetracted ? rows : live;

  return (
    <div className="space-y-3">
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {live.length === 0 && !adding && (
        <p className="text-sm text-slate-500">
          Nothing stated yet, so everything has to be discovered. Recon only finds
          what a wordlist guesses; anything you already know about this target
          belongs here.
        </p>
      )}

      {shown.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {shown.map((row) => (
            <div
              key={row.id}
              className={`flex items-start gap-3 px-3 py-2 ${row.retracted_at ? 'opacity-50' : ''}`}
            >
              <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  {row.label}
                  {/* The citation string, shown rather than composed here, so
                      what the operator reads is exactly what an agent writes. */}
                  <code className="rounded bg-slate-100 px-1 text-[11px] font-normal text-slate-500">
                    {row.citation}
                  </code>
                  {row.retracted_at && (
                    <span className="rounded border border-slate-300 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">
                      withdrawn
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs leading-snug text-slate-600">{row.statement}</div>
                {row.values?.length > 0 && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    <span className="text-slate-400">{row.values.length} value(s): </span>
                    <code className="break-all">
                      {row.values.slice(0, 6).join(', ')}
                      {row.values.length > 6 ? `, and ${row.values.length - 6} more` : ''}
                    </code>
                  </div>
                )}
              </div>
              {isPentester && !row.retracted_at && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => retract(row)}
                  className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                  title="Withdraw. The agents stop seeing it; a report that already cited it still can."
                >
                  <Undo2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {withdrawn.length > 0 && (
        <button
          type="button"
          onClick={() => setShowRetracted((v) => !v)}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          {showRetracted ? 'Hide' : 'Show'} {withdrawn.length} withdrawn
        </button>
      )}

      {adding ? (
        <AddFactForm onSubmit={create} onCancel={() => setAdding(false)} busy={busy} />
      ) : isPentester && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" /> Add fact
        </button>
      )}
      <div className="mt-2 flex items-start text-xs text-slate-500">
        <span>What counts as a fact?</span>
        <ExamplesHelp />
      </div>
    </div>
  );
}
